import React from 'react';
import { Chip, TextField, Typography, Box } from '@mui/material';
import {
  CheckCircleRounded,
  CancelRounded,
  WarningAmberRounded,
  EditRounded,
  CloseRounded,
  ImageNotSupportedRounded,
  AttachMoneyRounded,
  Inventory2Rounded,
  LocalOfferRounded,
} from '@mui/icons-material';
import InputBox from '../../../atomic/inputbox/InputBox';
import { statusTypeLabel } from './constants';
import formHeaderStyles from './components/styles.module.css';

// ── Constants ──────────────────────────────────────────────────────────────────

const BRAND = 'rgb(24, 20, 54)';
const ORANGE = 'rgb(255, 123, 41)';

// ── Helpers ────────────────────────────────────────────────────────────────────

const getStatusChipAppearance = (status_type) => {
  if (!status_type) return { bg: '#e65100', fg: '#ffffff', Icon: WarningAmberRounded };
  const n = String(status_type).toLowerCase();
  if (n === 'approved') return { bg: '#2e7d32', fg: '#ffffff', Icon: CheckCircleRounded };
  if (n === 'rejected' || n === 'reject' || n.includes('reject'))
    return { bg: '#e53935', fg: '#ffffff', Icon: CancelRounded };
  if (n.includes('pending') || n.includes('review'))
    return { bg: '#e65100', fg: '#ffffff', Icon: WarningAmberRounded };
  return { bg: '#e65100', fg: '#ffffff', Icon: WarningAmberRounded };
};

const promotionLabels = {
  buy_one_get_one_free: 'Buy 1 Get 1 Free',
  buy_two_get_one_free: 'Buy 2 Get 1 Free',
  buy_one_get_half_price: 'Buy 1 Get Half Price',
};

const toTitleCaseLocal = (str) =>
  str ? str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';

const getActivePromotion = (product_promotion) => {
  if (!product_promotion) return null;
  const key = Object.keys(product_promotion).find((k) => product_promotion[k]);
  return key ? promotionLabels[key] || toTitleCaseLocal(key) : null;
};

const hasValue = (v) => v && v !== 'select' && String(v).trim() !== '';
const noop = () => {};
const readonlyInputProps = { readOnly: true };
const readonlyInputSx = {
  '& .MuiInputLabel-root': {
    color: '#9e9e9e !important',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#9e9e9e !important',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#f7f7f7',
    pointerEvents: 'none',
    '& fieldset': {
      borderColor: '#d3d3d3 !important',
    },
    '&:hover fieldset': {
      borderColor: '#d3d3d3 !important',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#d3d3d3 !important',
    },
  },
  '& .MuiOutlinedInput-input': {
    color: '#666',
    WebkitTextFillColor: '#666',
    userSelect: 'none',
    cursor: 'default',
  },
  '& .MuiOutlinedInput-inputMultiline': {
    userSelect: 'none',
  },
};

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ── Main Component ─────────────────────────────────────────────────────────────

const KitchenListProductPreviewPanel = ({ product, onClose, onEdit }) => {
  if (!product) return null;

  const price = parseFloat(product.price || product.selling_price || 0).toFixed(2);
  const qty = String(product.available_quantity ?? 0);
  const isOutOfStock = parseInt(qty, 10) <= 0;
  const promotion = getActivePromotion(product.product_promotion);
  const menuTypeLabel = product.product_type === 'meal_item' ? 'Combo Menu' : 'Regular Menu';

  const ingredients = (product.ingredients || []).filter(Boolean);
  const allergens = (product.allergic_ingredients || []).filter(Boolean);
  const toppings = (product.extra_toppings || []).filter((t) => t.topping_name);
  const servingDays = (product.serving_days || []).filter(Boolean);
  const specialMarkings = (product.special_marking || []).filter(Boolean);

  const normalizedStatus = product.status_type ? String(product.status_type).toLowerCase() : '';
  const showRejectNote = Boolean(product.status_notes) && normalizedStatus.includes('reject');
  const chipAppearance = product.status_type ? getStatusChipAppearance(product.status_type) : null;
  const ChipIcon = chipAppearance?.Icon;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        backgroundColor: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* ── Dark Banner Header ───────────────────────────────────────────────── */}
      <div style={bannerStyle}>
        <div style={bannerRowStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={bannerTitleStyle}>
              {toTitleCaseLocal(product.product_name || 'Product Preview')}
            </div>
            <div style={bannerSubtitleStyle}>
              <span style={{ color: '#555', fontFamily: 'Inter Medium' }}>{product.product_sku}</span>
              <span style={{ color: '#bbb', margin: '0 6px' }}>·</span>
              <span style={{ color: '#888' }}>{menuTypeLabel}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignSelf: 'flex-start' }}>
            {onEdit && (
              <button onClick={onEdit} style={bannerEditBtnStyle} title='Edit Product'>
                <EditRounded sx={{ fontSize: 14 }} />
                <span>Edit</span>
              </button>
            )}
            <button onClick={onClose} style={bannerCloseBtnStyle} title='Close'>
              <CloseRounded sx={{ fontSize: 16 }} />
            </button>
          </div>
        </div>

        {product.status_type && chipAppearance && (
          <div
            className={formHeaderStyles.statusSection}
            style={{ width: '100%', marginTop: 10 }}
          >
            <div style={{ marginBottom: 8 }}>
              <Chip
                icon={<ChipIcon sx={{ fontSize: 18, color: 'inherit' }} />}
                label={statusTypeLabel[product.status_type] || product.status_type}
                size='small'
                sx={{
                  backgroundColor: chipAppearance.bg,
                  color: chipAppearance.fg,
                  border: 'none',
                  height: '22px',
                  fontSize: '11px',
                  fontFamily: 'Inter Medium',
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            </div>
            {showRejectNote && (
              <div className={formHeaderStyles.statusNote}>Note: {product.status_notes}</div>
            )}
          </div>
        )}
      </div>

      {/* ── Scrollable Body ─────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div
          style={{ display: 'flex', flexDirection: 'column', padding: '20px', boxSizing: 'border-box', width: '100%' }}
        >
          {/* ── Product Name ── */}
          <div
            style={{ display: 'flex', flexDirection: 'row', width: '100%', gap: 10, alignItems: 'center' }}
          >
            <div style={{ width: '100%', marginBottom: '10px', marginTop: '10px' }}>
              <InputBox
                type='text'
                label='Product name'
                size='small'
                sx={{ ...readonlyInputSx, width: '100%', height: 'auto', margin: '10px 0 5px' }}
                name='productname'
                value={toTitleCaseLocal(product.product_name || '')}
                onChange={noop}
                InputProps={readonlyInputProps}
              />
            </div>

            {/* ── Recipe With ── */}
            {hasValue(product.recipe_with) && (
              <InputBox
                type='text'
                label='Recipe with'
                size='small'
                sx={{ ...readonlyInputSx, width: '100%', height: 'auto' }}
                name='recipewith'
                value={toTitleCaseLocal(product.recipe_with)}
                onChange={noop}
                InputProps={readonlyInputProps}
              />
            )}
          </div>

          {/* ── Category + Special Marking ── */}
          {(hasValue(product.product_category) || specialMarkings.length > 0) && (
            <div
              style={{ display: 'flex', flexDirection: 'row', width: '100%', margin: '10px 0', gap: 10 }}
            >
              {hasValue(product.product_category) && (
                <InputBox
                  type='text'
                  label='Product Category'
                  size='small'
                  sx={{ ...readonlyInputSx, flex: 1, height: 'auto' }}
                  name='category'
                  value={toTitleCaseLocal(product.product_category)}
                  onChange={noop}
                  InputProps={readonlyInputProps}
                />
              )}
              {specialMarkings.length > 0 && (
                <InputBox
                  type='text'
                  label='Special Marking'
                  size='small'
                  sx={{ ...readonlyInputSx, flex: 1, height: 'auto' }}
                  name='specialmarking'
                  value={specialMarkings.map(toTitleCaseLocal).join(', ')}
                  onChange={noop}
                  InputProps={readonlyInputProps}
                />
              )}
            </div>
          )}

          {/* ── Description + Serving Days ── */}
          {(hasValue(product.product_description) || servingDays.length > 0) && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                alignItems: 'stretch',
                justifyContent: 'space-between',
                margin: '10px 0 20px',
                gap: 12,
              }}
            >
              {hasValue(product.product_description) && (
                <TextField
                  label='Description'
                  multiline
                  value={product.product_description}
                  onChange={noop}
                  InputProps={readonlyInputProps}
                  InputLabelProps={{
                    sx: {
                      color: '#9e9e9e',
                      display: 'flex',
                      '&.Mui-focused': { color: '#9e9e9e' },
                    },
                  }}
                  sx={{
                    ...readonlyInputSx,
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      alignItems: 'flex-start',
                    },
                    '& .MuiOutlinedInput-input': {
                      height: '100% !important',
                      overflow: 'auto !important',
                      boxSizing: 'border-box',
                      resize: 'none',
                    },
                  }}
                />
              )}

              {/* Serving days — row list with dot indicators */}
              {servingDays.length > 0 && (
                <Box
                  sx={{
                    padding: '12px 12px 12px 16px',
                    border: '1px solid',
                    borderColor: 'gray',
                    borderRadius: 2,
                    width: 230,
                    flexShrink: 0,
                    boxSizing: 'border-box',
                  }}
                >
                  <Typography variant='h6' sx={{ mb: 0, color: BRAND, fontSize: '16px' }}>
                    Serving Days
                  </Typography>
                  <div
                    style={{
                      fontFamily: 'Inter Regular',
                      letterSpacing: '0.5px',
                      fontSize: '13px',
                      margin: '5px 0 12px 0',
                      color: 'gray',
                    }}
                  >
                    Days this product is available
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}
                  >
                    {DAYS_OF_WEEK.map((day) => {
                      const active = servingDays.some(
                        (d) => d.toLowerCase() === day.toLowerCase()
                      );
                      return (
                        <div
                          key={day}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '4px 8px',
                            borderRadius: '6px',
                            backgroundColor: active ? '#fff3ea' : 'transparent',
                          }}
                        >
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: active ? '#000' : '#ddd',
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontSize: '13px',
                              fontFamily: active ? 'Inter Medium' : 'Inter Regular',
                              color: active ? BRAND : 'gray',
                            }}
                          >
                            {day}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </Box>
              )}
            </div>
          )}

          {/* ── Price + Quantity + Serving Temperature ── */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: 'row',
              width: '100%',
              gap: 10,
            }}
          >
            <InputBox
              type='text'
              label='Price'
              size='small'
              sx={{ ...readonlyInputSx, flex: '0 0 140px', height: 'auto', marginBottom: '10px' }}
              name='price'
              value={`$${price}`}
              onChange={noop}
              InputProps={readonlyInputProps}
            />
            <InputBox
              type='text'
              label='Quantity'
              size='small'
              sx={{ ...readonlyInputSx, flex: '0 0 110px', height: 'auto', marginBottom: '10px' }}
              name='quantity'
              value={qty}
              onChange={noop}
              InputProps={readonlyInputProps}
            />
            {hasValue(product.serving_temperature) && (
              <InputBox
                type='text'
                label='Serving Temperature'
                size='small'
                sx={{ ...readonlyInputSx, flex: 1, height: 'auto', marginBottom: '8px' }}
                name='servingtemp'
                value={toTitleCaseLocal(product.serving_temperature)}
                onChange={noop}
                InputProps={readonlyInputProps}
              />
            )}
          </div>

          {/* ── Product Image + Offer — single row, two columns ── */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              marginTop: '10px',
              gap: 12,
              alignItems: 'stretch',
            }}
          >
            {/* Left: Product image */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                border: '1px solid lightgray',
                borderRadius: '8px',
                padding: '16px',
                flex: 1,
                minHeight: '240px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                backgroundColor: '#fafafa',
                boxSizing: 'border-box',
              }}
            >
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.product_name}
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <ImageNotSupportedRounded sx={{ fontSize: 52, color: '#ddd' }} />
                  <span style={{ fontSize: '12px', fontFamily: 'Inter Regular', color: '#bbb' }}>
                    No product image
                  </span>
                </div>
              )}
            </div>

            {/* Right: Offer — always shown */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                border: promotion ? '1px solid #e0c97a' : '1px solid lightgray',
                borderRadius: '8px',
                padding: '20px',
                backgroundColor: promotion ? '#fffdf9' : '#fafafa',
                boxSizing: 'border-box',
                justifyContent: promotion ? 'flex-start' : 'center',
                alignItems: promotion ? 'flex-start' : 'center',
              }}
            >
              {promotion ? (
                <>
                  <Typography variant='h6' sx={{ color: BRAND, fontSize: '15px', mb: 1 }}>
                    🎁 Active Offer
                  </Typography>
                  <div
                    style={{
                      fontFamily: 'Inter Regular',
                      letterSpacing: '0.5px',
                      fontSize: '13px',
                      margin: '0 0 14px 0',
                      color: 'gray',
                    }}
                  >
                    This product has an active promotional offer applied.
                  </div>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 16px',
                      backgroundColor: BRAND,
                      color: '#fff',
                      borderRadius: '4px',
                      fontFamily: 'Inter Medium',
                      fontSize: '13px',
                      borderBottom: `3px solid ${ORANGE}`,
                    }}
                  >
                    <LocalOfferRounded sx={{ fontSize: 15 }} />
                    {promotion}
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                    color: '#ccc',
                  }}
                >
                  <LocalOfferRounded sx={{ fontSize: 44, color: '#ddd' }} />
                  <span style={{ fontSize: '13px', fontFamily: 'Inter Medium', color: '#bbb' }}>
                    No Offer
                  </span>
                  <span style={{ fontSize: '12px', fontFamily: 'Inter Regular', color: '#ccc', textAlign: 'center' }}>
                    No promotion applied to this product
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── Extra Toppings ── */}
          {toppings.length > 0 && (
            <div style={{ width: '100%', marginTop: '16px' }}>
              <div
                style={{
                  fontSize: '14px',
                  fontFamily: 'Inter Medium',
                  color: BRAND,
                  marginBottom: '10px',
                  paddingLeft: '8px',
                  borderLeft: `3px solid ${ORANGE}`,
                }}
              >
                Extra Toppings
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {toppings.map((topping, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                    <InputBox
                      type='text'
                      label='Topping name'
                      size='small'
                      sx={{ ...readonlyInputSx, flex: 2, height: 'auto' }}
                      name={`topping_name_${i}`}
                      value={toTitleCaseLocal(topping.topping_name)}
                      onChange={noop}
                      InputProps={readonlyInputProps}
                    />
                    <InputBox
                      type='text'
                      label='Price'
                      size='small'
                      sx={{ ...readonlyInputSx, flex: 1, height: 'auto' }}
                      name={`topping_price_${i}`}
                      value={`$${parseFloat(topping.topping_price || 0).toFixed(2)}`}
                      onChange={noop}
                      InputProps={readonlyInputProps}
                    />
                    <InputBox
                      type='text'
                      label='Quantity'
                      size='small'
                      sx={{ ...readonlyInputSx, flex: 1, height: 'auto' }}
                      name={`topping_qty_${i}`}
                      value={String(topping.topping_available_quantity ?? 0)}
                      onChange={noop}
                      InputProps={readonlyInputProps}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Ingredients + Allergens ── */}
          {(ingredients.length > 0 || allergens.length > 0) && (
            <div
              style={{
                display: 'flex',
                width: '100%',
                flexDirection: 'row',
                marginTop: '10px',
                justifyContent: 'space-between',
                border: '1px solid lightgray',
                borderRadius: '3px',
                padding: '24px',
                gap: 20,
                boxSizing: 'border-box',
              }}
            >
              {ingredients.length > 0 && (
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: '14px',
                      fontFamily: 'Inter Medium',
                      color: BRAND,
                      marginBottom: '10px',
                      paddingLeft: '8px',
                      borderLeft: `3px solid #4caf50`,
                    }}
                  >
                    Ingredients
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {ingredients.map((ing, i) => (
                      <span
                        key={i}
                        style={{
                          display: 'inline-block',
                          padding: '3px 10px',
                          backgroundColor: '#e8f5e9',
                          border: '1px solid #c8e6c9',
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontFamily: 'Inter Medium',
                          color: '#2e7d32',
                        }}
                      >
                        {toTitleCaseLocal(ing)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {allergens.length > 0 && (
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: '14px',
                      fontFamily: 'Inter Medium',
                      color: BRAND,
                      marginBottom: '10px',
                      paddingLeft: '8px',
                      borderLeft: `3px solid #f44336`,
                    }}
                  >
                    Allergens
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {allergens.map((a, i) => (
                      <span
                        key={i}
                        style={{
                          display: 'inline-block',
                          padding: '3px 10px',
                          backgroundColor: '#fce4ec',
                          border: '1px solid #ef9a9a',
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontFamily: 'Inter Medium',
                          color: '#c62828',
                        }}
                      >
                        {toTitleCaseLocal(a)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Size + Cuisine + Ingredients Image ── */}
          {(hasValue(product.size) || hasValue(product.cuisine) || product.ingredients_image) && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                flexDirection: 'row',
                marginTop: '20px',
                gap: 16,
              }}
            >
              {(hasValue(product.size) || hasValue(product.cuisine)) && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    border: '1px solid lightgray',
                    borderRadius: '3px',
                    padding: '10px',
                    boxSizing: 'border-box',
                    flexDirection: 'row',
                    gap: 10,
                  }}
                >
                  {hasValue(product.size) && (
                    <InputBox
                      type='text'
                      label='Size'
                      size='small'
                      sx={{ ...readonlyInputSx, width: '100%', height: 'auto', margin: '10px 0' }}
                      name='size'
                      value={toTitleCaseLocal(product.size)}
                      onChange={noop}
                      InputProps={readonlyInputProps}
                    />
                  )}
                  {hasValue(product.cuisine) && (
                    <InputBox
                      type='text'
                      label='Cuisine'
                      size='small'
                      sx={{ ...readonlyInputSx, width: '100%', height: 'auto', margin: '10px 0' }}
                      name='cuisine'
                      value={toTitleCaseLocal(product.cuisine)}
                      onChange={noop}
                      InputProps={readonlyInputProps}
                    />
                  )}
                </div>
              )}

              {product.ingredients_image && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: '1px solid lightgray',
                    borderRadius: '5px',
                    padding: '10px',
                    width: '200px',
                    height: '250px',
                    flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <img
                    src={product.ingredients_image}
                    alt='Ingredients'
                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '4px' }}
                  />
                </div>
              )}
            </div>
          )}

          {/* ── Edit CTA ── */}
          {onEdit && (
            <div
              style={{
                width: '100%',
                padding: '20px',
                marginTop: '20px',
                borderRadius: '3px',
                border: `1px solid ${BRAND}`,
                backgroundColor: 'rgb(233, 245, 190)',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  fontSize: '13px',
                  fontFamily: 'Inter Regular',
                  color: '#555',
                  marginBottom: '12px',
                  paddingLeft: '4px',
                }}
              >
                Want to make changes? Click below to open the edit form.
              </div>
              <button
                onClick={onEdit}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '14px',
                  backgroundColor: BRAND,
                  border: 'none',
                  borderBottom: `3px solid ${ORANGE}`,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Inter Medium',
                  color: '#ffffff',
                  letterSpacing: '0.5px',
                }}
              >
                <EditRounded sx={{ fontSize: 16 }} />
                EDIT PRODUCT
              </button>
            </div>
          )}

          <div style={{ height: 28 }} />
        </div>
      </div>
    </div>
  );
};

// ── Banner styles ──────────────────────────────────────────────────────────────

const bannerStyle = {
  backgroundColor: '#ffffff',
  padding: '18px 20px',
  borderBottom: '1px solid #ebebeb',
  flexShrink: 0,
};

const bannerRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
};

const bannerTitleStyle = {
  fontSize: '17px',
  fontFamily: 'Inter Bold',
  color: BRAND,
  marginBottom: 4,
};

const bannerSubtitleStyle = {
  fontSize: '12px',
  fontFamily: 'Inter Regular',
  color: '#888',
};

const bannerEditBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  padding: '0 14px',
  height: '32px',
  backgroundColor: BRAND,
  border: `1px solid ${BRAND}`,
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13px',
  fontFamily: 'Inter Medium',
  color: '#ffffff',
  flexShrink: 0,
};

const bannerCloseBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  backgroundColor: '#f5f5f5',
  border: '1px solid #e0e0e0',
  borderRadius: '6px',
  cursor: 'pointer',
  color: '#555',
  flexShrink: 0,
};

export default KitchenListProductPreviewPanel;
